import { UserRole } from "@/__generated__/globalTypes"
import determineUserRole from "@/components/common/shared/DetermineBaseRoute"
import MODULE_TYPES from "@/constants/MODULE_TYPES"
import useSnackbar from "@/helpers/add-snackbar"
import { usePersistedState } from "@/helpers/state"
import { gql, useLazyQuery, useMutation } from "@apollo/client"
import { Button } from "@material-ui/core"
import { makeStyles } from "@material-ui/core/styles"
import { MenuOpen } from "@material-ui/icons"
import { Field, FormikHelpers } from "formik"
import React, { useState } from "react"
import { useHistory } from "react-router"
import { Preferences } from "../auth/AuthContext"
import useAuth from "../auth/useAuth"
import FormDialog from "../util/dialog/FormDialog"
import GraphError from "../util/error/GraphError"
import AutoComplete from "../util/form/fields/AutoComplete"
import Loading from "../util/Loading"
import { getOrganisationsSimple } from "./__generated__/getOrganisationsSimple"
import {
  StartImpersonation,
  StartImpersonationVariables
} from "./organisations/__generated__/StartImpersonation"
import { START_IMPERSONATION } from "./organisations/ImpersonateUserDialog"

export const GET_ORGANISATIONS_SIMPLE = gql`
  query getOrganisationsSimple {
    organisations(first: 2000, page: 1) {
      data {
        id
        name
        ref
      }
      paginatorInfo {
        currentPage
        total
      }
    }
  }
`
const useStyles = makeStyles(theme => ({
  recentOrgContainer: {
    display: "flex",
    gap: "8px",
    marginBottom: "8px"
  },
  recentOrgButton: {
    padding: "4px 8px",
    minWidth: "auto",
    fontSize: "0.8rem",
    backgroundColor: "#f5f5f5",
    "&:hover": {
      backgroundColor: "#e0e0e0"
    }
  },
  recentOrgSection: {
    marginBottom: theme.spacing(2),
    borderBottom: `1px solid ${theme.palette.divider}`,
    paddingBottom: theme.spacing(2)
  },
  recentOrgLabel: {
    color: theme.palette.text.secondary,
    fontSize: "0.875rem",
    marginBottom: theme.spacing(1)
  },
  dialogRecentOrgContainer: {
    display: "flex",
    gap: "8px",
    flexWrap: "wrap"
  },
  dialogRecentOrgButton: {
    padding: "4px 12px",
    fontSize: "0.875rem",
    backgroundColor: theme.palette.background.default,
    "&:hover": {
      backgroundColor: theme.palette.action.hover
    }
  },
  loadingContainer: {
    paddingTop: theme.spacing(10)
  }
}))

const ChangeOrg = () => {
  const classes = useStyles()
  const { user } = useAuth()
  const history = useHistory()
  const { addSnackbar } = useSnackbar()
  const [open, setOpen] = useState<boolean>(false)
  const [, setPreferences] = usePersistedState<Preferences | undefined>(
    "preferences",
    true
  )
  const [recentOrgs, setRecentOrgs] = usePersistedState<
    Array<{ id: string; name: string }>
  >("recentOrgs", true, [])
  let redirect = ""

  const [startImpersonation, { client }] = useMutation<
    StartImpersonation,
    StartImpersonationVariables
  >(START_IMPERSONATION)

  const [triggerGetOrganisationsSimple, { loading, data, error }] =
    useLazyQuery<getOrganisationsSimple>(GET_ORGANISATIONS_SIMPLE, {
      context: { module: MODULE_TYPES.ADMIN }
    })

  if (error) return <GraphError error={error} />

  const organisations = data?.organisations?.data ?? []

  const organisationOptions =
    organisations?.map(e => ({
      label: `${e?.name}${e?.ref ? ` (${e.ref})` : ""}`,
      value: e?.id,
      ref: e?.ref ?? e.name,
      name: e?.name,
      searchText: `${e?.name} ${e?.ref || ""}`.toLowerCase()
    })) ?? []

  const handleImpersonation = async (orgId: string) => {
    try {
      const result = await startImpersonation({
        variables: {
          organisationId: orgId,
          role: UserRole.admin
        }
      })
      const assets = result?.data?.startImpersonation?.organisation?.assets
      const baseRouteNew = result?.data?.startImpersonation?.organisation?.type
      redirect = determineUserRole(baseRouteNew)

      const selectedOrg = organisations.find(org => org.id === orgId)
      if (selectedOrg) {
        const updatedRecentOrgs = [
          { id: selectedOrg.id, name: selectedOrg.ref || selectedOrg.name },
          ...recentOrgs.filter(org => org.id !== orgId)
        ].slice(0, 4)
        setRecentOrgs(updatedRecentOrgs)
      }

      setPreferences({
        asOfDate: user?.preferences?.asOfDate ?? user?.asOfDate ?? "",
        assetId: assets?.data[0]?.id ?? "",
        assetType: assets?.data[0]?.type ?? null,
        useLatestAsOfDate: user?.preferences?.useLatestAsOfDate ?? true
      })
      client.resetStore()
      addSnackbar("User Impersonated")

      const paths = {
        owner: "overview",
        manager: "clients",
        default: "assetList"
      }
      const path = paths[redirect as keyof typeof paths] || paths.default
      setOpen(false)
      history.push(`/${redirect}/${path}`)
    } catch (error) {
      addSnackbar("Failed to impersonate user", "error")
    } finally {
      window.location.reload()
    }
  }
  return (
    <>
      <Button
        color="inherit"
        variant="outlined"
        size="small"
        onClick={() => {
          triggerGetOrganisationsSimple()
          setOpen(true)
        }}
        startIcon={<MenuOpen />}
      >
        Change
      </Button>
      <FormDialog
        title={"Impersonate"}
        initialValues={organisationOptions?.[0] ?? []}
        onClickLabel="Impersonate"
        displayFormState
        open={open}
        onClose={() => setOpen(false)}
        onSubmit={(
          values,
          { setSubmitting, setErrors }: FormikHelpers<unknown>
        ) => {
          const orgId = values?.ref?.value
          handleImpersonation(orgId)
            .then(() => setSubmitting(false))
            .catch(error => {
              setSubmitting(false)
              if (error.graphQLErrors?.[0]?.extensions?.validation) {
                setErrors(error.graphQLErrors[0].extensions.validation)
              }
            })
        }}
      >
        {recentOrgs.length > 0 && (
          <div className={classes.recentOrgSection}>
            <div className={classes.recentOrgLabel}>Recent Organisations</div>
            <div className={classes.dialogRecentOrgContainer}>
              {recentOrgs.map(org => (
                <Button
                  key={org.id}
                  className={classes.dialogRecentOrgButton}
                  variant="contained"
                  size="small"
                  onClick={() => handleImpersonation(org.id)}
                >
                  {org.name}
                </Button>
              ))}
            </div>
          </div>
        )}
        {loading ? (
          <div className={classes.loadingContainer}>
            <Loading />
          </div>
        ) : (
          <Field
            name={"ref"}
            label={"Organisation"}
            component={AutoComplete}
            margin="normal"
            options={organisationOptions}
            fullWidth
            variant="filled"
            disabled={loading || organisations.length === 0}
            getOptionLabel={(option: any) => option.label || ""}
            isOptionEqualToValue={(option: any, value: any) =>
              option.value === value.value
            }
            filterOptions={(options: any[], params: any) => {
              const searchTerm = params.inputValue.toLowerCase()
              return options.filter(
                (option: any) =>
                  (option.name &&
                    option.name.toLowerCase().includes(searchTerm)) ||
                  (option.ref && option.ref.toLowerCase().includes(searchTerm))
              )
            }}
          />
        )}
      </FormDialog>
    </>
  )
}

export default ChangeOrg
